/**
 * Radius circles (M8). Draw a circle by center + radius, or drop N rings around
 * selected points; the inside set is computed client-side and the circle + count
 * persist in `analysis.radii`. Pure over geometry.ts — no provider call, so it
 * ships first to validate the overlay + spec foundation.
 */

import type { Point, PointSet } from "@/lib/map/pointset";
import { haversineMeters, MILES_TO_METERS, pointsInRadius } from "./geometry";
import type { RadiusCenter, RadiusCircle } from "./spec";

let radiusSeq = 0;
function radiusId(): string {
  radiusSeq += 1;
  return `radius_${Date.now().toString(36)}_${radiusSeq}`;
}

/** Resolve a radius center (explicit [lng,lat] or a rowId) to a coordinate. */
export function resolveCenter(center: RadiusCenter, pointSet: PointSet): [number, number] | null {
  if (Array.isArray(center)) return center;
  const p = pointSet.points.find((pt) => pt.id === center);
  return p ? [p.lng, p.lat] : null;
}

/** Human label for a radius in miles ("10 mi"). */
export function radiusLabel(radiusMeters: number): string {
  const miles = radiusMeters / MILES_TO_METERS;
  const rounded = miles >= 10 ? Math.round(miles) : Math.round(miles * 10) / 10;
  return `${rounded} mi`;
}

/**
 * Build one radius circle from a center + radius, computing its inside set from
 * the point set. `center` may be an explicit coordinate or a rowId.
 */
export function buildRadiusCircle(
  pointSet: PointSet,
  center: RadiusCenter,
  radiusMeters: number,
  opts?: { id?: string; label?: string },
): RadiusCircle {
  const resolved = resolveCenter(center, pointSet);
  const insideRowIds = resolved ? pointsInRadius(pointSet.points, resolved, radiusMeters) : [];
  return {
    id: opts?.id ?? radiusId(),
    center,
    radiusMeters,
    insideRowIds,
    label: opts?.label ?? radiusLabel(radiusMeters),
  };
}

/**
 * N concentric rings around a single point (row), inner→outer. Each ring's
 * inside set is cumulative (a point inside the 5-mile ring is also inside the
 * 10-mile one), matching how "3 rings at 5/10/15 mi" reads on the map.
 */
export function ringsAroundPoint(
  pointSet: PointSet,
  rowId: string,
  radiiMeters: number[],
): RadiusCircle[] {
  return [...radiiMeters]
    .sort((a, b) => a - b)
    .map((radiusMeters) => buildRadiusCircle(pointSet, rowId, radiusMeters));
}

/** N rings (same radii) around each of several selected points. */
export function ringsAroundPoints(
  pointSet: PointSet,
  rowIds: string[],
  radiiMeters: number[],
): RadiusCircle[] {
  return rowIds.flatMap((rowId) => ringsAroundPoint(pointSet, rowId, radiiMeters));
}

/** Distinct rows inside ANY of the given circles (dedup across overlaps). */
export function unionInside(circles: RadiusCircle[]): string[] {
  const set = new Set<string>();
  for (const c of circles) for (const id of c.insideRowIds) set.add(id);
  return [...set];
}

/** Recompute the inside set for a circle against a (possibly new) point set. */
export function recomputeRadius(circle: RadiusCircle, pointSet: PointSet): RadiusCircle {
  return buildRadiusCircle(pointSet, circle.center, circle.radiusMeters, {
    id: circle.id,
    label: circle.label,
  });
}

/** Distance in meters between two points, exposed for the draw interaction (drag → radius). */
export function metersBetween(a: [number, number], b: [number, number]): number {
  return haversineMeters(a, b);
}

/** Convenience: does this point fall inside the circle's radius? */
export function isInsideCircle(circle: RadiusCircle, point: Point, pointSet: PointSet): boolean {
  const resolved = resolveCenter(circle.center, pointSet);
  if (!resolved) return false;
  return haversineMeters(resolved, [point.lng, point.lat]) <= circle.radiusMeters;
}
